import { FormEvent } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { CheckoutShell } from "@/components/CheckoutShell"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

type AcceptProps = PageProps<{
  token: string
  account_name: string
  inviter_email: string
  invited_email: string
  existing_user: boolean
  logged_in_as_invitee: boolean
}>

export default function Accept() {
  const { props } = usePage<AcceptProps>()
  const form = useForm({ password: "" })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post(`/invitations/${props.token}`)
  }

  const needsPassword = !props.logged_in_as_invitee

  return (
    <>
      <Head title="Join your team on Flywrite">
        <meta name="description" content="Accept your Flywrite team invitation." />
        <meta property="og:title" content="Join your team on Flywrite" />
        <meta property="og:description" content="Accept your Flywrite team invitation." />
      </Head>
      <CheckoutShell maxWidth="max-w-md">
        <div className="callout">
          <h1>Join {props.account_name}</h1>
          <p className="mt-2 text-sm text-ink-body">
            <strong>{props.inviter_email}</strong> invited{" "}
            <strong>{props.invited_email}</strong> to their Flywrite team.
          </p>

          {props.errors?.base && (
            <p className="mt-4 text-sm text-danger-display">{props.errors.base}</p>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {needsPassword && (
              <div className="space-y-2">
                <label htmlFor="password">
                  {props.existing_user ? "Your password" : "Choose a password"}
                </label>
                <Input
                  id="password"
                  type="password"
                  autoComplete={props.existing_user ? "current-password" : "new-password"}
                  required
                  minLength={props.existing_user ? undefined : 7}
                  value={form.data.password}
                  aria-invalid={!!props.errors?.password}
                  onChange={(e) => form.setData("password", e.target.value)}
                />
                {props.errors?.password && (
                  <p className="text-xs text-danger-display">{props.errors.password}</p>
                )}
                <p className="text-xs text-ink-muted">
                  {props.existing_user
                    ? "You already have a Flywrite login with this email — enter its password."
                    : "This creates your Flywrite login."}
                </p>
              </div>
            )}
            <Button type="submit" disabled={form.processing} className="w-full">
              Accept invitation
            </Button>
          </form>
        </div>
      </CheckoutShell>
    </>
  )
}
